import { Box, Grid, Card, CardMedia, Typography, Tooltip, Dialog, Menu, MenuItem, useMediaQuery } from "@mui/material";
import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { useMemo, useEffect } from "react";
import { Favorite, ChatBubble } from "@mui/icons-material";
import ImageBox from "./ImageBox";
import type { IImage } from "../types/image";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Loading from "./Loading";
import axiosService from "../utils/axios";
import { updateImageBoxData } from "../utils/helpers/imageHelper";
import type { IComment } from "../types/comment";
import Theme from "../utils/theme";

interface IImageCardFeedProps {
    type: "username" | "recipeId";
    username?: string;
    recipeId?: string;
    setRecipeAvatarUrl?: (s: string) => void;
}

const ImageCardFeed: React.FC<IImageCardFeedProps> = ({ type, username, recipeId, setRecipeAvatarUrl }) => {
    const authState = useSelector((state: RootState) => state.auth);
    const accUser = authState.account?.username;

    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const isUser = authState.account?.username === username;

    const [images, setImages] = useState<IImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [openImageModal, setOpenImageModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const handleImageClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, index: number) => {
            setCurrentImageIndex(index);

            if (type === "username" || !isUser) {
                setOpenImageModal(true);
            } else {
                setAnchorEl(event.currentTarget);
            }
        },
        [isUser, type]
    );

    // Set a images data //
    const setCardImages = useCallback(
        (type: "likes" | "commentText" | "commentLike", comment?: IComment, commentId?: string) => {
            if (!accUser || (type === "commentText" && !comment) || (type === "commentLike" && !commentId)) return;

            setImages((prev: IImage[]) => {
                return updateImageBoxData(prev, currentImageIndex, type, accUser, comment, commentId);
            });
        },
        [accUser, currentImageIndex]
    );

    const handleImageMenuClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const observer = useRef<IntersectionObserver | null>(null);

    const fetchURL =
        type === "username"
            ? `${process.env.REACT_APP_API_URL}/images/fetchByUsername`
            : `${process.env.REACT_APP_API_URL}/images/fetchByRecipeId`;

    const fetchData = type === "username" ? username : recipeId;
    const fetchInitialImages = useCallback(async () => {
        try {
            setLoading(true);

            const res = await axios.get(fetchURL, {
                params: { cursor: null, limit: 10, fetchData },
            });

            setImages(res.data.body);
            setLoading(false);
        } catch (err) {
            console.error("Failed to fetch images", err);
            setLoading(false);
        }
    }, [fetchData, fetchURL]);

    useEffect(() => {
        fetchInitialImages();
    }, [fetchInitialImages]);

    const lastImageRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    const lastPost = images[images.length - 1];
                    const cursor = lastPost?.createdAt;

                    setLoading(true);
                    if (type === "username") {
                    }

                    axios
                        .get(fetchURL, {
                            params: { cursor: cursor, limit: 10, fetchData },
                        })
                        .then((res) => {
                            setImages((prev) => [...prev, ...res.data.body]);
                            setLoading(false);

                            if (res.data.body.length === 0) setHasMore(false);
                        });
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, hasMore, images, type, fetchURL, fetchData]
    );

    const closeImageModal = useCallback(() => {
        setOpenImageModal(false);
        fetchInitialImages();
    }, [fetchInitialImages]);

    const renderedImageGrid = useMemo(
        () =>
            images.length > 0 ? (
                images.map((image, index) => {
                    const reference = type === "username" ? (index === images.length - 1 ? lastImageRef : null) : null;
                    return (
                        <Grid size={isXs ? 6 : 4} key={index} ref={reference}>
                            <Card
                                onClick={(e) => handleImageClick(e, index)}
                                sx={{
                                    m: "0.5px",
                                    boxSizing: "border-box",
                                    cursor: "pointer",
                                    position: "relative",
                                    overflow: "hidden",
                                    borderRadius: isXs ? 0 : 2,
                                    "&:hover .overlay": {
                                        opacity: 1,
                                    },
                                    backgroundColor: "#f0f0f0",
                                    border: "1px solid black",
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    src={image.url}
                                    alt={image.caption}
                                    sx={{
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        objectFit: "contain",
                                        transition: "transform 0.3s",
                                        "&:hover": {
                                            transform: "scale(1.05)",
                                        },
                                    }}
                                />
                                <Box
                                    className="overlay"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        bgcolor: "rgba(0,0,0,0.6)",
                                        color: "#fff",
                                        opacity: 0,
                                        transition: "opacity 0.3s ease",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "flex-end",
                                        p: 2,
                                    }}
                                >
                                    <Typography variant="body1" noWrap>
                                        {image.caption || "Untitled"}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            fontSize: "0.8rem",
                                            mt: 0.5,
                                        }}
                                    >
                                        <Tooltip title="Likes">
                                            <Typography>
                                                <Favorite /> {image.likes.length}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title="Comments">
                                            <Typography>
                                                <ChatBubble /> {image.comments.length}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title="Posted">
                                            <Typography>
                                                {new Date(image.createdAt).toLocaleString(undefined, {
                                                    dateStyle: "short",
                                                    timeStyle: "short",
                                                })}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })
            ) : (
                <Typography sx={{ mt: 7, width: "100%", textAlign: "center", color: theme.palette.text.secondary }}>
                    Looks like there are no pictures in this Recipe Book!
                </Typography>
            ),
        [images, theme.palette.text.secondary, type, lastImageRef, isXs, handleImageClick]
    );

    const viewRecipeImage = useCallback(() => {
        handleImageMenuClose();
        setOpenImageModal(true);
    }, []);

    const setRecipeImageCover = useCallback(() => {
        handleImageMenuClose();
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/recipes/image/setCover`, {
                recipeId: recipeId,
                imageId: images[currentImageIndex]._id,
            })
            .then((res) => {
                if (setRecipeAvatarUrl !== undefined) {
                    setRecipeAvatarUrl(res.data.body.url);
                }
            });
    }, [currentImageIndex, images, recipeId, setRecipeAvatarUrl]);

    const deleteImage = useCallback(() => {
        handleImageMenuClose();
        const currentImageId = images[currentImageIndex]._id;

        axiosService.post(`${process.env.REACT_APP_API_URL}/images/delete`, { imageId: currentImageId }).then((res) => {
            setImages((prev) => prev.filter((image: IImage) => image._id !== currentImageId));
        });
    }, [currentImageIndex, images]);

    if (loading) {
        return (
            <Box sx={{ mt: 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Loading />
            </Box>
        );
    }

    return (
        <Box>
            <Grid container spacing={isXs ? 0 : 2}>
                {renderedImageGrid}
            </Grid>

            <Dialog maxWidth="lg" fullWidth open={openImageModal} onClose={() => closeImageModal()}>
                <Box sx={{ position: "relative" }}>
                    <ImageBox
                        link={type === "recipeId" ? images.map((image) => "/recipe/" + recipeId) : []}
                        images={images}
                        setImages={setCardImages}
                        currentImageIndex={currentImageIndex}
                        setCurrentImageIndex={setCurrentImageIndex}
                    />
                </Box>
            </Dialog>

            {type === "recipeId" && isUser && (
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleImageMenuClose}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <MenuItem onClick={() => viewRecipeImage()}>View</MenuItem>
                    <MenuItem onClick={() => deleteImage()}>Delete</MenuItem>
                    <MenuItem onClick={() => setRecipeImageCover()}>Set as Cover</MenuItem>
                </Menu>
            )}
        </Box>
    );
};

export default ImageCardFeed;
